import { useEffect, useMemo, useState } from 'react'
import { useLocation } from '@reach/router'

import { useClinic } from '~contexts/clinic-context'
import { useUserContext } from '~config/user-context'
import {
    GetClinicGroupsForUserQuery,
    useGetClinicGroupsForUserQuery,
} from '~graphql/generated/graphql'

// A clinic as returned from the clinic-group query. These are partial Clinic
// objects (CGCClinicFields); the full Clinic is re-hydrated by ClinicContext
// once selected.
export type SwitchableClinic =
    GetClinicGroupsForUserQuery['getClinicGroupsForUser'][number]['clinic_group_clinics'][number]['clinic']

type CandidateGroup = {
    id: string
    name: string
}

// Sections whose sub-tab (landing) pages should display the hot-swap toolbar.
const CONFIG_SECTION_PREFIXES = [
    '/analytics',
    '/booking',
    '/engagement',
    '/patient-portal',
    '/clinic',
    '/settings',
]

/**
 * The toolbar shows on the config "sub-tab" landing pages only (e.g.
 * /booking/services, /engagement/email-templates, /settings/general) and is
 * hidden on entity-specific / detail pages (anything deeper, e.g.
 * /booking/services/:id, /engagement/forms/edit/:id). Landing pages live at a
 * depth of two path segments; detail/create/edit pages are deeper.
 */
export function isConfigToolbarRoute(pathname: string): boolean {
    const path = pathname.replace(/\/+$/, '') || '/'
    const inSection = CONFIG_SECTION_PREFIXES.some(
        prefix => path === prefix || path.startsWith(`${prefix}/`),
    )
    if (!inSection) return false

    const segments = path.split('/').filter(Boolean)
    return segments.length <= 2
}

export type ClinicSwitcher = {
    show: boolean
    groups: CandidateGroup[]
    selectedGroupId: string | undefined
    setSelectedGroupId: (id: string) => void
    clinics: SwitchableClinic[]
    currentClinicId: string | undefined
    switchToClinic: (clinic: SwitchableClinic) => void
}

export function useClinicSwitcher(): ClinicSwitcher {
    const { pathname } = useLocation()
    const { userId } = useUserContext()
    const { clinic, clinicGroup, selectClinicById } = useClinic()

    const [selectedGroupId, setSelectedGroupId] = useState<string>()
    // Optimistically highlight the clinic the user just clicked while its full
    // data loads, so the selection feels instant even though the rendered
    // clinic only swaps once loading completes.
    const [pendingClinicId, setPendingClinicId] = useState<string>()

    const onConfigRoute = isConfigToolbarRoute(pathname)

    // Only relevant in single-clinic mode. Skip entirely when there is no
    // clinic, we're in clinic-group mode, or we're not on a config sub-tab.
    const skip = !userId || !clinic || !!clinicGroup || !onConfigRoute

    const { data: groupsData } = useGetClinicGroupsForUserQuery({
        variables: { userId: userId || '' },
        skip,
    })

    const userGroups = groupsData?.getClinicGroupsForUser

    // Clinic groups that (a) the logged-in user belongs to and (b) contain the
    // currently selected clinic.
    const candidateGroups = useMemo(() => {
        if (!clinic || !userGroups) return []
        return userGroups.filter(
            group =>
                !group.is_archived &&
                group.clinic_group_clinics.some(
                    cgc => cgc.clinic.id === clinic.id,
                ),
        )
    }, [userGroups, clinic])

    const candidateGroupIds = candidateGroups.map(g => g.id).join(',')
    const primaryGroupId = clinic?.settings?.primary_clinic_group_id

    // Default the active group to the clinic's primary group when available,
    // otherwise the first candidate. Keep the user's explicit choice if still
    // valid.
    useEffect(() => {
        if (!candidateGroups.length) {
            setSelectedGroupId(undefined)
            return
        }
        setSelectedGroupId(prev => {
            if (prev && candidateGroups.some(g => g.id === prev)) return prev
            const primary = candidateGroups.find(g => g.id === primaryGroupId)
            return primary?.id ?? candidateGroups[0].id
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateGroupIds, primaryGroupId])

    const selectedGroup = candidateGroups.find(g => g.id === selectedGroupId)

    // Every clinic in a group the user belongs to is reachable. Direct
    // clinic membership is not required, and the clinic is not listed twice
    // when they also have a clinic_admins row.
    const clinics = useMemo(() => {
        if (!selectedGroup || !clinic) return []

        const groupClinics = selectedGroup.clinic_group_clinics
            .map(cgc => cgc.clinic)
            .filter(c => !c.is_archived)

        const current = groupClinics.filter(c => c.id === clinic.id)
        const others = groupClinics
            .filter(c => c.id !== clinic.id)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

        return [...current, ...others]
    }, [selectedGroup, clinic])

    // Clear the optimistic highlight once the selected clinic has loaded.
    useEffect(() => {
        if (pendingClinicId && clinic?.id === pendingClinicId)
            setPendingClinicId(undefined)
    }, [clinic?.id, pendingClinicId])

    const switchToClinic = (next: SwitchableClinic) => {
        if (!next || next.id === clinic?.id) return
        setPendingClinicId(next.id)
        // Switch by id so ClinicContext keeps the current clinic rendered until
        // the new one's full data loads — the sidebar/header stay put and only
        // the page content reloads. We intentionally do not navigate so the
        // user stays on the same config page for the newly selected clinic.
        selectClinicById(next.id)
    }

    return {
        show:
            onConfigRoute && !!clinic && clinics.length > 1 && !!selectedGroup,
        groups: candidateGroups.map(g => ({ id: g.id, name: g.name })),
        selectedGroupId,
        setSelectedGroupId,
        clinics,
        currentClinicId: pendingClinicId ?? clinic?.id,
        switchToClinic,
    }
}
